import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../layouts/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { claimsAPI } from '../lib/api';
import { formatDate, formatCurrency, getStatusClass, downloadBlob } from '../lib/utils';
import {
  FileText,
  Plus,
  Eye,
  Download,
  Send,
  XCircle,
  Loader2,
  Calendar,
  MapPin
} from 'lucide-react';

export default function MyClaims() {
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchClaims();
  }, []);

  const fetchClaims = async () => {
    setLoading(true);
    try {
      const response = await claimsAPI.getMyClaims();
      setClaims(response.data);
    } catch (error) {
      toast.error('Failed to load claims');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async (claimId) => {
    setActionLoading(claimId);
    try {
      await claimsAPI.applyClaim(claimId);
      toast.success('Claim submitted for approval');
      fetchClaims();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit claim');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (claimId) => {
    if (!window.confirm('Are you sure you want to cancel this claim?')) return;
    
    setActionLoading(claimId);
    try {
      await claimsAPI.cancelClaim(claimId);
      toast.success('Claim cancelled');
      fetchClaims();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel claim');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownloadPDF = async (claim) => {
    setActionLoading(claim.id);
    try {
      const response = await claimsAPI.downloadPDF(claim.id);
      downloadBlob(response.data, `AdvanceClaim_${claim.claim_number}.pdf`);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="my-claims-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">My Claims</h1>
            <p className="text-muted-foreground">View and manage your advance claims</p>
          </div>
          <Button onClick={() => navigate('/claims/new')} data-testid="new-claim-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Claim
          </Button>
        </div>

        {/* Claims List */}
        {claims.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileText className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No claims yet</h3>
              <p className="text-muted-foreground mb-4">Create your first advance claim to get started</p>
              <Button onClick={() => navigate('/claims/new')} data-testid="create-first-claim">
                <Plus className="w-4 h-4 mr-2" />
                Create Claim
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {claims.map((claim) => (
              <Card key={claim.id} className="card-hover" data-testid={`claim-card-${claim.claim_number}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    {/* Claim Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg">{claim.claim_number}</h3>
                        <Badge className={getStatusClass(claim.status)}>
                          {claim.status}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          Travel: {formatDate(claim.date_of_travel)}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {claim.project_site_location}
                        </span>
                      </div>
                    </div>

                    {/* Amount */}
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Claim Amount</p>
                      <p className="text-xl font-bold text-primary">
                        {formatCurrency(claim.calculations?.advance_claim_amount)}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/claims/${claim.id}`)}
                        data-testid={`view-claim-${claim.claim_number}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>

                      {claim.status === 'LIVE' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleApply(claim.id)}
                            disabled={actionLoading === claim.id}
                            data-testid={`apply-claim-${claim.claim_number}`}
                          >
                            {actionLoading === claim.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Send className="w-4 h-4 mr-1" />
                                Apply
                              </>
                            )}
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleCancel(claim.id)}
                            disabled={actionLoading === claim.id}
                            data-testid={`cancel-claim-${claim.claim_number}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Cancel
                          </Button>
                        </>
                      )}

                      {['SENT FOR APPROVAL', 'APPROVED', 'PAID'].includes(claim.status) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadPDF(claim)}
                          disabled={actionLoading === claim.id}
                          data-testid={`download-pdf-${claim.claim_number}`}
                        >
                          {actionLoading === claim.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Download className="w-4 h-4 mr-1" />
                              PDF
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
